<template>
  <StatsWidget
    :title="$t('stats_dashboard.reject_reasons.title')"
    :description="description"
    icon="xmark"
    tone="rose"
    :loading="widget.loading"
    :error="widget.error"
    :has-data="hasData"
    :empty-message="$t('stats_dashboard.reject_reasons.empty')"
    :span-full="true"
    @refresh="$emit('refresh')"
  >
    <ul class="stats-reject-reasons__list">
      <li
        v-for="(row, idx) in reasons"
        :key="row.code"
        class="stats-reject-reasons__row"
      >
        <span
          class="stats-reject-reasons__rank"
          :class="rankClass(idx)"
        >{{ idx + 1 }}</span>
        <div class="stats-reject-reasons__body">
          <div class="stats-reject-reasons__meta">
            <span class="stats-reject-reasons__label">{{ reasonLabel(row.code) }}</span>
            <div class="stats-reject-reasons__metrics">
              <span class="stats-reject-reasons__count">
                {{
                  $t('stats_dashboard.reject_reasons.count_proposals', {
                    n: formatInteger(row.count),
                  })
                }}
                · {{ row.share }}%
              </span>
              <span class="stats-reject-reasons__value">
                {{ formatCurrency(row.value) }}
              </span>
            </div>
          </div>
          <div class="stats-reject-reasons__track">
            <div
              class="stats-reject-reasons__fill"
              :style="{ width: row.share + '%' }"
            ></div>
          </div>
        </div>
      </li>
    </ul>
  </StatsWidget>
</template>

<script>
  import { computed, getCurrentInstance } from 'vue';
  import StatsWidget from './StatsWidget.vue';
  import { formatCurrency, formatInteger } from '@/utils/statsFormat';

  export default {
    name: 'StatsRejectReasons',

    components: { StatsWidget },

    props: {
      widget: { type: Object, required: true },
    },

    emits: ['refresh'],

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const rejectedCount = computed(() => props.widget.data?.rejected_count || 0);
      const rejectedValue = computed(() => props.widget.data?.rejected_value || 0);

      const reasons = computed(() => {
        const rows = props.widget.data?.reasons || [];
        const total = rejectedCount.value;
        return rows.map((row) => ({
          ...row,
          share: total ? Math.round(((row.count || 0) / total) * 100) : 0,
        }));
      });

      const hasData = computed(() => reasons.value.length > 0);

      const description = computed(() => {
        if (!hasData.value) {
          return $t('stats_dashboard.reject_reasons.description');
        }
        return $t('stats_dashboard.reject_reasons.total', {
          n: formatInteger(rejectedCount.value),
          value: formatCurrency(rejectedValue.value),
        });
      });

      const reasonLabel = (code) => {
        const key = `proposals.reject_reasons.${code}`;
        const translated = $t(key);
        return translated === key ? $t('proposals.reject_reasons.unknown') : translated;
      };

      const rankClass = (idx) => {
        if (idx === 0) return 'stats-reject-reasons__rank--gold';
        if (idx === 1) return 'stats-reject-reasons__rank--silver';
        if (idx === 2) return 'stats-reject-reasons__rank--bronze';
        return '';
      };

      return {
        reasons,
        hasData,
        description,
        reasonLabel,
        rankClass,
        formatCurrency,
        formatInteger,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .stats-reject-reasons {
    &__list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__row {
      display: grid;
      grid-template-columns: 1.75rem 1fr;
      align-items: start;
      gap: 0.75rem;
      padding: 0.375rem 0.375rem 0.125rem;
      border-radius: 0.5rem;
    }

    &__rank {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1.5rem;
      height: 1.5rem;
      margin-top: 0.125rem;
      border-radius: 50%;
      font-size: 0.75rem;
      font-weight: 700;
      color: var(--slate-600);
      background: var(--slate-100);
      font-variant-numeric: tabular-nums;
      flex-shrink: 0;

      &--gold {
        color: #b45309;
        background: linear-gradient(135deg, #fef3c7, #fcd34d);
      }
      &--silver {
        color: #475569;
        background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
      }
      &--bronze {
        color: #9a3412;
        background: linear-gradient(135deg, #fed7aa, #fdba74);
      }
    }

    &__body {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
    }

    &__meta {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      gap: 0.75rem;
    }

    &__label {
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-800);
      @include ellipsis(1);
    }

    &__metrics {
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 0.0625rem;
      flex-shrink: 0;
    }

    &__count {
      font-size: 0.6875rem;
      color: var(--slate-500);
      font-variant-numeric: tabular-nums;
    }

    &__value {
      font-size: 0.875rem;
      font-weight: 700;
      color: var(--slate-900);
      font-variant-numeric: tabular-nums;
    }

    &__track {
      height: 0.375rem;
      background: var(--slate-100);
      border-radius: 999px;
      overflow: hidden;
    }

    &__fill {
      height: 100%;
      background: var(--rose-400, #fb7185);
      border-radius: 999px;
      min-width: 0;
    }
  }
</style>
